import React, { useState, useEffect, useCallback } from 'react';
import { subscriptionsAPI } from '../../services/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import Badge from '../../components/common/Badge';
import Pagination from '../../components/common/Pagination';
import PermissionGuard from '../../components/common/PermissionGuard';
import Spinner from '../../components/common/Spinner';
import toast from 'react-hot-toast';

const Subscriptions = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await subscriptionsAPI.getAll({ page, limit: 10, status: statusFilter });
      setSubs(res.data.data);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load subscriptions'); }
    finally { setLoading(false); }
  }, [page, statusFilter]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  const handleCancel = async (id) => {
    const reason = prompt('Cancellation reason (optional):');
    if (reason === null) return;
    try { await subscriptionsAPI.cancel(id, { reason }); toast.success('Subscription cancelled'); fetchSubs(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleRenew = async (id) => {
    try { await subscriptionsAPI.renew(id); toast.success('Subscription renewed'); fetchSubs(); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleProcessExpired = async () => {
    try {
      const res = await subscriptionsAPI.processExpired();
      toast.success(res.data.message);
      fetchSubs();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
          <option value="">All Status</option>
          {['active','trial','expired','cancelled','suspended'].map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        <PermissionGuard module="subscriptions" action="update">
          <button onClick={handleProcessExpired} className="ml-auto px-4 py-2 bg-orange-600 text-white rounded-lg text-sm font-medium hover:bg-orange-700 transition">
            Process Expired
          </button>
        </PermissionGuard>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? <div className="p-12"><Spinner /></div> : (
          <>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-3 font-medium">Customer</th>
                  <th className="px-6 py-3 font-medium">Plan</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                  <th className="px-6 py-3 font-medium">Start Date</th>
                  <th className="px-6 py-3 font-medium">End Date</th>
                  <th className="px-6 py-3 font-medium">Amount</th>
                  <th className="px-6 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {subs.map(sub => (
                  <tr key={sub._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="font-medium">{sub.customer?.name}</p>
                      <p className="text-xs text-gray-500">{sub.customer?.email}</p>
                    </td>
                    <td className="px-6 py-4">{sub.plan?.name || '—'}</td>
                    <td className="px-6 py-4"><Badge status={sub.status} /></td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(sub.startDate)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatDate(sub.endDate)}</td>
                    <td className="px-6 py-4 font-medium">{formatCurrency(sub.plan?.price)}</td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <PermissionGuard module="subscriptions" action="update">
                          {['active','trial'].includes(sub.status) && (
                            <button onClick={() => handleCancel(sub._id)} className="text-red-600 hover:underline text-xs">Cancel</button>
                          )}
                          {['expired','cancelled'].includes(sub.status) && (
                            <button onClick={() => handleRenew(sub._id)} className="text-green-600 hover:underline text-xs">Renew</button>
                          )}
                        </PermissionGuard>
                      </div>
                    </td>
                  </tr>
                ))}
                {subs.length === 0 && <tr><td colSpan={7} className="px-6 py-12 text-center text-gray-400">No subscriptions found</td></tr>}
              </tbody>
            </table>
            <Pagination pagination={pagination} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
};

export default Subscriptions;
